import { useState } from 'react';

import { Icons } from '@/assets/icons/icons';
import { cn } from '@/lib/utils';

import type { ActionCardAttachment } from '@littlebird/action-cards';

const Attachment = ({ attachment }: { attachment: ActionCardAttachment }) => {
  const [imageFailed, setImageFailed] = useState(false);
  const label = attachment.title?.trim() || 'Linear attachment';
  const targetUrl = attachment.fallbackUrl ?? attachment.url;

  if (!imageFailed) {
    return (
      <a href={targetUrl} target="_blank" rel="noreferrer" className="block">
        <img
          src={attachment.url}
          alt={label}
          loading="lazy"
          onError={() => setImageFailed(true)}
          className="max-h-64 max-w-full rounded-lg border border-border object-contain"
        />
      </a>
    );
  }

  return (
    <a
      href={targetUrl}
      target="_blank"
      rel="noreferrer"
      className="inline-flex max-w-full items-center gap-1.5 rounded-md bg-surface-hover px-2.5 py-1.5 text-sm text-foreground hover:bg-surface-active"
    >
      <Icons.paperclip className="size-3.5 shrink-0" aria-hidden="true" />
      <span className="truncate">{label}</span>
    </a>
  );
};

export const ActionCardAttachments = ({
  attachments,
  labelled = true,
  className,
}: {
  attachments: readonly ActionCardAttachment[];
  labelled?: boolean;
  className?: string;
}) => {
  if (!attachments.length) return null;
  const thumbs = attachments.map((attachment) => (
    <Attachment key={attachment.url} attachment={attachment} />
  ));
  if (!labelled) {
    return (
      <span
        className={cn('flex flex-wrap gap-2', className)}
        aria-label="Issue images"
      >
        {thumbs}
      </span>
    );
  }
  return (
    <span className="flex items-start gap-3.5">
      <span className="w-24 shrink-0 pt-2 text-xs text-foreground-muted sm:w-28">
        Issue images
      </span>
      <span className="flex min-w-0 flex-1 flex-col items-start gap-2">
        {thumbs}
      </span>
    </span>
  );
};
